import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent } from 'react'
import {
  Bell,
  Check,
  ExternalLink,
  Inbox,
  Loader2,
  MailOpen,
  MoreVertical,
  Search,
  Star,
  Trash2,
  Undo2,
} from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUserRole } from '@/hooks/useUserRole'
import { canViewProfileMessages } from '../utils/profileMessagesAccess'
import { cn } from '@/lib/utils/helpers'
import { markAnnouncementRead } from '@/lib/api/announcementsService'
import { markReleaseNoteRead } from '@/lib/api/releaseNotesService'
import {
  deleteCenterNotifications,
  fetchNotificationCenter,
  fetchProfileNotificationCounts,
  fetchProfileNotifications,
  fetchUserNotificationDetail,
  markAllNotificationsRead,
  restoreCenterNotifications,
  updateProfileNotification,
} from '@/lib/api/notificationService'
import type {
  CenterMessage,
  NotificationCounts,
  NotificationDetail,
  NotificationFolder,
} from '@/types/notification'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { buildNotificationDetailSection } from '@/features/invitee/utils/notificationDetailHelpers'
import {
  buildNotificationPreviewText,
  stripHtml,
  VERSION_TYPE_COLOR_CLASS,
  VERSION_TYPE_LABELS,
} from '@/features/invitee/utils/userDrawerHelpers'
import { normalizeAnnouncementDescriptionHtml } from '@/features/system-announcement/utils/normalizeAnnouncementHtml'
import {
  categoryFromCenterKind,
  decodeNotificationText,
  formatNotificationDetailDate,
  formatNotificationListDate,
  getAvatarTone,
  getNotificationCategoryLabel,
  getNotificationTitle,
  getSenderInitials,
  getSenderName,
  isCenterUnread,
  mapUserNotificationToCenter,
  mergeNotificationDetail,
  parseProfileMessageDeepLink,
  processReleaseNoteImages,
  resolveCenterKind,
  toUserNotification,
  type NotificationCategory,
} from '../utils/messageCenterHelpers'

const viewFolders: Array<{
  id: NotificationFolder
  label: string
  countKey: keyof NotificationCounts
  icon: typeof Inbox
}> = [
  { id: 'all', label: 'All notifications', countKey: 'inbox', icon: Bell },
  { id: 'inbox', label: 'Unread', countKey: 'unread', icon: Inbox },
  { id: 'starred', label: 'Starred', countKey: 'starred', icon: Star },
  { id: 'deleted', label: 'Deleted', countKey: 'deleted', icon: Trash2 },
]

const categories: Array<{
  id: NotificationCategory
  label: string
  dot: string
}> = [
  { id: 'all', label: 'All', dot: 'bg-[#2f7de1]' },
  { id: 'mentions', label: 'Mentions', dot: 'bg-[#8b5cf6]' },
  { id: 'updates', label: "What's New", dot: 'bg-[#22c55e]' },
  { id: 'system', label: 'System Announcements', dot: 'bg-[#f59e0b]' },
]

const LIST_PAGE_SIZE = 15
const LIST_PANE_HEIGHT = 'h-[36rem]'

const emptyCounts: NotificationCounts = {
  inbox: 0,
  unread: 0,
  starred: 0,
  read: 0,
  deleted: 0,
}

function isStarredMessage(item: Pick<CenterMessage, 'starred'>) {
  return item.starred === 1 || item.starred === '1'
}

function categorySource(category: NotificationCategory) {
  if (category === 'mentions') return 'user'
  if (category === 'updates') return 'whats_new'
  if (category === 'system') return 'system'
  return 'all'
}

function selectionKey(item: Pick<CenterMessage, 'kind' | 'id'>) {
  return `${item.kind}:${item.id}`
}

function parseSelectionKey(key: string): Pick<CenterMessage, 'kind' | 'id'> {
  const [kind, rawId] = key.split(':')
  return { kind: kind as CenterMessage['kind'], id: Number(rawId) }
}

function SenderAvatar({
  firstName,
  lastName,
  size = 'md',
}: {
  firstName?: string | null
  lastName?: string | null
  size?: 'sm' | 'md' | 'lg'
}) {
  const name = getSenderName(firstName, lastName)
  return (
    <div
      className={cn(
        'grid shrink-0 place-items-center rounded-full font-bold',
        size === 'sm' && 'size-10 text-sm',
        size === 'md' && 'size-12 text-base',
        size === 'lg' && 'size-14 text-lg',
        getAvatarTone(name),
      )}
    >
      {getSenderInitials(firstName, lastName)}
    </div>
  )
}

export function UserMessagesTab({ userId }: { userId: number }) {
  const navigate = useNavigate()
  const { roleName } = useUserRole()
  const allowAllKind = canViewProfileMessages(roleName)
  const [searchParams] = useSearchParams()
  const deepLink = useMemo(
    () => parseProfileMessageDeepLink(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- serialize so a new URLSearchParams instance does not retrigger
    [searchParams.toString()],
  )
  const [folder, setFolder] = useState<NotificationFolder>('all')
  const [category, setCategory] = useState<NotificationCategory>(() =>
    deepLink ? categoryFromCenterKind(deepLink.kind) : 'all',
  )
  const [search, setSearch] = useState('')
  const [submittedSearch, setSubmittedSearch] = useState('')
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const detailRequestIdRef = useRef(0)
  const pendingFirstSelectRef = useRef(!deepLink)
  const deepLinkHandledRef = useRef<string | null>(null)
  const skipAutoSelectRef = useRef(false)
  const detailSourceRef = useRef<CenterMessage | null>(null)
  const loadedQueryRef = useRef('')
  const pageRef = useRef(1)
  const lastPageRef = useRef(1)
  const loadingMoreLockRef = useRef(false)
  const listRequestIdRef = useRef(0)
  const listScrollRef = useRef<HTMLDivElement | null>(null)
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null)
  const [messages, setMessages] = useState<CenterMessage[]>([])
  const [counts, setCounts] = useState<NotificationCounts>(emptyCounts)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [markingAllRead, setMarkingAllRead] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [detail, setDetail] = useState<NotificationDetail | null>(null)
  const [detailSource, setDetailSource] = useState<CenterMessage | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [pendingDeleteKeys, setPendingDeleteKeys] = useState<string[]>([])
  const [deleting, setDeleting] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)
  const [pendingMove, setPendingMove] = useState<'unread' | 'read' | 'undelete' | null>(null)
  const [moving, setMoving] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  detailSourceRef.current = detailSource

  const loadCounts = useCallback(async () => {
    setCounts(await fetchProfileNotificationCounts(userId))
  }, [userId])

  const fetchMessagesPage = useCallback(
    async (pageNum: number) => {
      if (folder === 'all' || folder === 'inbox' || folder === 'deleted') {
        const response = await fetchNotificationCenter({
          folder,
          source: categorySource(category),
          page: pageNum,
          perPage: LIST_PAGE_SIZE,
          q: submittedSearch,
          allowAllKind,
        })
        return {
          data: Array.isArray(response.data) ? response.data : [],
          last: response.last || 1,
          total: response.total || 0,
        }
      }
      const response = await fetchProfileNotifications({
        userId,
        folder,
        page: pageNum,
        perPage: LIST_PAGE_SIZE,
        q: submittedSearch,
      })
      return {
        data: (response.data ?? []).map(mapUserNotificationToCenter),
        last: response.last || 1,
        total: response.total || 0,
      }
    },
    [allowAllKind, category, folder, submittedSearch, userId],
  )

  const loadList = useCallback(async () => {
    const requestId = ++listRequestIdRef.current
    setLoading(true)
    setLoadingMore(false)
    loadingMoreLockRef.current = true
    pageRef.current = 1
    listScrollRef.current?.scrollTo({ top: 0 })
    try {
      const response = await fetchMessagesPage(1)
      if (listRequestIdRef.current !== requestId) return
      setMessages(response.data)
      lastPageRef.current = response.last
      setTotal(response.total)
      setSelectedKeys([])
      loadedQueryRef.current = submittedSearch
    } catch (error) {
      if (listRequestIdRef.current !== requestId) return
      setMessages([])
      lastPageRef.current = 1
      setTotal(0)
      loadedQueryRef.current = submittedSearch
      showApiError(error)
    } finally {
      if (listRequestIdRef.current === requestId) {
        setLoading(false)
        loadingMoreLockRef.current = false
      }
    }
  }, [fetchMessagesPage, submittedSearch])

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || loadingMoreLockRef.current) return
    if (pageRef.current >= lastPageRef.current) return
    const requestId = listRequestIdRef.current
    loadingMoreLockRef.current = true
    setLoadingMore(true)
    const nextPage = pageRef.current + 1
    try {
      const response = await fetchMessagesPage(nextPage)
      if (listRequestIdRef.current !== requestId) return
      setMessages((prev) => {
        const seen = new Set(prev.map((item) => `${item.kind}-${item.id}`))
        return [
          ...prev,
          ...response.data.filter((item) => !seen.has(`${item.kind}-${item.id}`)),
        ]
      })
      pageRef.current = nextPage
      lastPageRef.current = response.last
      setTotal(response.total)
    } catch (error) {
      if (listRequestIdRef.current === requestId) {
        showApiError(error)
      }
    } finally {
      if (listRequestIdRef.current === requestId) {
        setLoadingMore(false)
        loadingMoreLockRef.current = false
      }
    }
  }, [fetchMessagesPage, loading, loadingMore])

  useEffect(() => {
    void loadList()
  }, [loadList])

  useEffect(() => {
    const root = listScrollRef.current
    const target = loadMoreSentinelRef.current
    if (!root || !target || loading) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadMore()
      },
      { root, rootMargin: '120px', threshold: 0 },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [loadMore, loading, messages.length])

  useEffect(() => {
    void loadCounts().catch(showApiError)
  }, [loadCounts])

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    }
  }, [])

  const handleSearchChange = (value: string, immediate = false) => {
    setSearch(value)
    pendingFirstSelectRef.current = true
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    const commitSearch = () => {
      pendingFirstSelectRef.current = true
      setSubmittedSearch(value)
    }
    if (immediate) commitSearch()
    else searchDebounceRef.current = setTimeout(commitSearch, 500)
  }

  const handleSearchPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text')
    if (pasted === '') return
    event.preventDefault()
    const start = event.currentTarget.selectionStart ?? search.length
    const end = event.currentTarget.selectionEnd ?? search.length
    handleSearchChange(`${search.slice(0, start)}${pasted}${search.slice(end)}`, true)
  }

  const changeFolder = (next: NotificationFolder) => {
    setFolder(next)
    setDetail(null)
    setDetailSource(null)
    setSelectedKeys([])
    setMoreOpen(false)
  }

  const changeCategory = (next: NotificationCategory) => {
    setCategory(next)
    setDetail(null)
    setDetailSource(null)
    setSelectedKeys([])
    setMoreOpen(false)
  }

  const markLocalRead = (kind: CenterMessage['kind'], id: number) => {
    setMessages((prev) =>
      prev.map((row) => (row.kind === kind && row.id === id ? { ...row, is_read: '1' } : row)),
    )
  }

  const markLocalUnread = (kind: CenterMessage['kind'], id: number) => {
    setMessages((prev) =>
      prev.map((row) => (row.kind === kind && row.id === id ? { ...row, is_read: null } : row)),
    )
  }

  const openDetail = async (item: CenterMessage) => {
    const requestId = ++detailRequestIdRef.current
    const kind = resolveCenterKind(item.kind)
    const selected = { ...item, kind }
    setMoreOpen(false)
    setDetailLoading(false)
    setDetailSource(selected)
    setDetail(kind === 'user' ? toUserNotification(selected) : null)
    try {
      if (kind === 'whats_new') {
        await markReleaseNoteRead(item.id)
        if (detailRequestIdRef.current !== requestId) return
        setDetail(null)
        setDetailSource({ ...selected, is_read: '1' })
        markLocalRead(kind, item.id)
      } else if (kind === 'system') {
        await markAnnouncementRead(item.id)
        if (detailRequestIdRef.current !== requestId) return
        setDetail(null)
        setDetailSource({ ...selected, is_read: '1' })
        markLocalRead(kind, item.id)
      } else {
        const response = await fetchUserNotificationDetail(item.id)
        if (detailRequestIdRef.current !== requestId) return
        setDetailSource({ ...selected, is_read: '1' })
        setDetail(mergeNotificationDetail(toUserNotification(selected), response))
        markLocalRead(kind, item.id)
      }
      if (detailRequestIdRef.current === requestId) {
        await loadCounts()
      }
    } catch (error) {
      if (detailRequestIdRef.current === requestId) {
        console.error('Failed to enrich notification detail', error)
        markLocalRead(kind, item.id)
      }
    }
  }

  useEffect(() => {
    if (!deepLink) return
    pendingFirstSelectRef.current = false
    skipAutoSelectRef.current = true
    setFolder('all')
    setCategory(categoryFromCenterKind(deepLink.kind))
  }, [deepLink])

  useEffect(() => {
    if (!deepLink || loading) return
    const key = `${deepLink.kind}:${deepLink.id}`
    const match = messages.find(
      (item) => resolveCenterKind(item.kind) === deepLink.kind && item.id === deepLink.id,
    )
    if (match) {
      if (deepLinkHandledRef.current === key) return
      deepLinkHandledRef.current = key
      skipAutoSelectRef.current = true
      pendingFirstSelectRef.current = false
      void openDetail(match)
      return
    }
    if (pageRef.current < lastPageRef.current) {
      void loadMore()
    }
  }, [deepLink, loading, loadMore, messages])

  const buildMovePayload = (
    action: 'starred' | 'deleted' | 'read' | 'unread' | 'not_starred',
  ) => {
    const payload: Record<string, number> = { userId, [action]: 1 }
    if (folder === 'deleted' && (action === 'read' || action === 'unread')) {
      payload.not_deleted = 1
    }
    if (folder === 'starred' && action === 'read') payload.not_starred = 1
    return payload
  }

  const moveMessage = async (
    notificationId: number,
    action: 'starred' | 'deleted' | 'read' | 'unread' | 'not_starred',
  ) => {
    await updateProfileNotification(notificationId, buildMovePayload(action))
    if (detailSource?.kind === 'user' && detailSource.id === notificationId) {
      setDetail(null)
      setDetailSource(null)
      setMoreOpen(false)
    }
    await Promise.all([loadList(), loadCounts()])
  }

  const toggleInboxStar = async (item: CenterMessage) => {
    if (item.kind !== 'user') return
    const nextStarred = !isStarredMessage(item)
    await updateProfileNotification(
      item.id,
      nextStarred ? { userId, starred: 1 } : { userId, not_starred: 1 },
    )
    if (detailSource?.kind === 'user' && detailSource.id === item.id) {
      setDetailSource({ ...detailSource, starred: nextStarred ? 1 : 0 })
    }
    await Promise.all([loadList(), loadCounts()])
  }

  const requestBulkMove = (action: 'unread' | 'read' | 'undelete') => {
    if (selectedKeys.length === 0) return
    setPendingMove(action)
    setMoveOpen(true)
  }

  const applySelectedAction = async (
    action: 'unread' | 'read',
    keys: string[],
  ) => {
    const payload = buildMovePayload(action)
    await Promise.all(
      keys.map((key) => {
        const { kind, id } = parseSelectionKey(key)
        if (kind === 'whats_new') {
          if (action === 'read') return markReleaseNoteRead(id, 1)
          if (action === 'unread') return markReleaseNoteRead(id, 0)
          return Promise.resolve()
        }
        if (kind === 'system') {
          if (action === 'read') return markAnnouncementRead(id, 1)
          if (action === 'unread') return markAnnouncementRead(id, 0)
          return Promise.resolve()
        }
        return updateProfileNotification(id, payload)
      }),
    )
  }

  const moveSelected = async () => {
    if (!pendingMove || selectedKeys.length === 0) return
    setMoving(true)
    try {
      if (pendingMove === 'undelete') {
        await restoreCenterNotifications(selectedKeys.map(parseSelectionKey))
        showSuccess(
          selectedKeys.length === 1
            ? 'Notification restored successfully'
            : 'Notifications restored successfully',
        )
      } else {
        await applySelectedAction(pendingMove, selectedKeys)
        showSuccess(
          pendingMove === 'unread'
            ? 'Notifications marked as unread'
            : 'Notifications moved to Read',
        )
      }
      const actedKeys = selectedKeys
      const actedMove = pendingMove
      setMoveOpen(false)
      setPendingMove(null)
      if (detailSource && actedKeys.includes(selectionKey(detailSource))) {
        setDetail(null)
        setDetailSource(null)
      }
      if (folder === 'inbox' && actedMove === 'read') {
        setMessages((prev) =>
          prev.filter((item) => !actedKeys.includes(selectionKey(item))),
        )
        skipAutoSelectRef.current = true
      }
      setSelectedKeys([])
      listRequestIdRef.current += 1
      await Promise.all([loadList(), loadCounts()])
    } catch (error) {
      showApiError(error)
    } finally {
      setMoving(false)
    }
  }

  const undeleteCurrent = async () => {
    if (!detailSource) return
    try {
      await restoreCenterNotifications([
        { kind: detailSource.kind, id: detailSource.id },
      ])
      showSuccess('Notification restored successfully')
      setDetail(null)
      setDetailSource(null)
      setMoreOpen(false)
      await Promise.all([loadList(), loadCounts()])
    } catch (error) {
      showApiError(error)
    }
  }

  const updateDetail = async (
    action: 'starred' | 'deleted' | 'read' | 'unread' | 'not_starred',
  ) => {
    if (!detailSource) return
    if ((action === 'starred' || action === 'not_starred') && detailSource.kind !== 'user') {
      return
    }
    try {
      if (action === 'unread') {
        if (detailSource.kind === 'user') {
          await updateProfileNotification(detailSource.id, buildMovePayload('unread'))
        } else if (detailSource.kind === 'whats_new') {
          await markReleaseNoteRead(detailSource.id, 0)
        } else {
          await markAnnouncementRead(detailSource.id, 0)
        }
        const unreadSource = { ...detailSource, is_read: null }
        setDetailSource(unreadSource)
        markLocalUnread(detailSource.kind, detailSource.id)
        setMoreOpen(false)
        skipAutoSelectRef.current = true
        pendingFirstSelectRef.current = false
        showSuccess('Notification marked as unread')
        await Promise.all([loadList(), loadCounts()])
        return
      }
      if (detailSource.kind === 'user') {
        await moveMessage(detailSource.id, action)
        return
      }
      if (action === 'deleted') {
        await deleteCenterNotifications([{ kind: detailSource.kind, id: detailSource.id }])
      } else if (action === 'read') {
        if (detailSource.kind === 'whats_new') {
          await markReleaseNoteRead(detailSource.id, 1)
        } else {
          await markAnnouncementRead(detailSource.id, 1)
        }
      } else {
        return
      }
      setDetail(null)
      setDetailSource(null)
      setMoreOpen(false)
      await Promise.all([loadList(), loadCounts()])
    } catch (error) {
      showApiError(error)
    }
  }

  const requestDelete = (keys: string[]) => {
    if (keys.length === 0) return
    setPendingDeleteKeys(keys)
    setDeleteOpen(true)
  }

  const deleteSelected = async () => {
    const keys = pendingDeleteKeys.length > 0 ? pendingDeleteKeys : []
    if (keys.length === 0) return
    setDeleting(true)
    try {
      await deleteCenterNotifications(keys.map(parseSelectionKey))
      showSuccess(
        keys.length === 1
          ? 'Notification deleted successfully'
          : 'Notifications deleted successfully',
      )
      setDeleteOpen(false)
      setPendingDeleteKeys([])
      setSelectedKeys([])
      if (detailSource && keys.includes(selectionKey(detailSource))) {
        setDetail(null)
        setDetailSource(null)
      }
      await Promise.all([loadList(), loadCounts()])
    } catch (error) {
      showApiError(error)
    } finally {
      setDeleting(false)
    }
  }

  const handleMarkAllRead = async () => {
    if (markingAllRead || counts.unread === 0) return
    setMarkingAllRead(true)
    try {
      await markAllNotificationsRead()
      showSuccess("All notifications marked 'Read' successfully!")
      if (folder === 'inbox') {
        setMessages([])
        setTotal(0)
        setDetail(null)
        setDetailSource(null)
        skipAutoSelectRef.current = true
      }
      setSelectedKeys([])
      listRequestIdRef.current += 1
      await Promise.all([loadList(), loadCounts()])
    } catch (error) {
      showApiError(error)
    } finally {
      setMarkingAllRead(false)
    }
  }

  const visibleNotifications = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return messages
    return messages.filter((item) => {
      const sender = getSenderName(item.first_name, item.last_name)
      return [item.title, item.preview, sender, item.body].some((value) =>
        String(value ?? '').toLowerCase().includes(query),
      )
    })
  }, [messages, search])

  useEffect(() => {
    if (loading) return
    if (deepLink && deepLinkHandledRef.current !== `${deepLink.kind}:${deepLink.id}`) {
      return
    }
    if (skipAutoSelectRef.current) {
      skipAutoSelectRef.current = false
      pendingFirstSelectRef.current = false
      return
    }
    if (visibleNotifications.length === 0) {
      detailRequestIdRef.current += 1
      setDetail(null)
      setDetailSource(null)
      setMoreOpen(false)
      setDetailLoading(false)
      return
    }

    const first = visibleNotifications[0]
    const current = detailSourceRef.current
    const isFirstSelected = current?.kind === first.kind && current?.id === first.id
    const stillVisible = Boolean(
      current &&
        visibleNotifications.some((item) => item.kind === current.kind && item.id === current.id),
    )
    const shouldSelectFirst = pendingFirstSelectRef.current || !stillVisible

    if (shouldSelectFirst && !isFirstSelected) {
      void openDetail(first)
    }
    if (loadedQueryRef.current === submittedSearch && search === submittedSearch) {
      pendingFirstSelectRef.current = false
    }
  }, [deepLink, loading, search, submittedSearch, visibleNotifications])

  const selectableKeys = visibleNotifications.map((item) => selectionKey(item))
  const allSelected =
    selectableKeys.length > 0 && selectableKeys.every((key) => selectedKeys.includes(key))
  const section = useMemo(
    () =>
      detail && detailSource?.kind === 'user'
        ? buildNotificationDetailSection(toUserNotification(detailSource), detail)
        : null,
    [detail, detailSource],
  )
  const selectedKey = detailSource ? `${detailSource.kind}-${detailSource.id}` : null
  const canRestore = folder === 'deleted'

  const goToTarget = (href: string) => {
    const [pathname, hash] = href.split('#')
    navigate(hash ? { pathname, hash: `#${hash}` } : pathname)
  }

  return (
    <div
      data-testid="profile-messages-tab"
      className="grid min-h-0 gap-4 text-[#17233b] lg:h-[36rem] lg:grid-cols-[220px_minmax(260px,400px)_minmax(0,1fr)] lg:items-stretch"
    >
      <aside className="flex h-auto min-h-0 flex-col overflow-y-auto rounded-2xl border border-[#dfe6ef] bg-white p-3 lg:h-full">
        <p className="px-3 pb-2 pt-1 text-[11px] font-bold uppercase tracking-[.14em] text-[#8491a5]">
          View
        </p>
        <nav className="space-y-1">
          {viewFolders.map(({ id, label, countKey, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => changeFolder(id)}
              className={cn(
                'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition',
                folder === id
                  ? 'bg-[#e8f1ff] text-[#174a87]'
                  : 'text-[#536176] hover:bg-[#f4f7fb]',
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="flex-1">{label}</span>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs',
                  folder === id ? 'bg-white' : 'bg-[#eef2f6]',
                )}
              >
                {counts[countKey] ?? 0}
              </span>
            </button>
          ))}
        </nav>

        <div className="mx-3 my-4 h-px bg-[#e8edf3]" />
        <p className="px-3 pb-2 text-[11px] font-normal uppercase tracking-[.14em] text-[#8491a5]">
          Category
        </p>
        <nav className="space-y-0.5">
          {categories.map(({ id, label, dot }) => (
            <button
              key={id}
              type="button"
              onClick={() => changeCategory(id)}
              className={cn(
                'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-[15px] leading-snug transition-colors',
                category === id
                  ? 'font-bold text-[#17233b]'
                  : 'font-normal text-[#6b7c93] hover:text-[#17233b]',
              )}
            >
              <span className={cn('size-2 rounded-full', dot)} />
              {label}
            </button>
          ))}
        </nav>
      </aside>

      <section
        className={cn(
          `flex ${LIST_PANE_HEIGHT} min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-[#dfe6ef] bg-white lg:h-full`,
          detailSource && 'hidden lg:flex',
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 px-5 pt-5">
          <div>
            <h2 className="text-xl font-bold">Notifications</h2>
            <p className="mt-0.5 text-sm text-[#75839a]">
              {total} notification{total === 1 ? '' : 's'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void handleMarkAllRead()}
            disabled={markingAllRead || counts.unread === 0 || folder === 'deleted'}
            className="inline-flex items-center gap-2 rounded-xl border border-[#dfe6ef] bg-white px-3 py-2 text-sm font-semibold text-[#174a87] shadow-sm hover:bg-[#f4f7fb] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {markingAllRead ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            Mark all read
          </button>
        </div>

        <div className="shrink-0 border-b border-[#e5e7eb] px-5 pb-5 pt-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8491a5]" />
            <input
              type="text"
              value={search}
              onChange={(event) => handleSearchChange(event.target.value)}
              onPaste={handleSearchPaste}
              placeholder="Search notifications"
              aria-label="Search notifications"
              className="w-full rounded-xl border border-[#dfe6ef] bg-white py-2.5 pl-11 pr-4 text-sm text-[#17233b] shadow-sm placeholder:text-[#8491a5] focus:border-[#174a87] focus:outline-none focus:ring-2 focus:ring-[#dce9fb]"
            />
          </div>
        </div>

        {selectedKeys.length > 0 && (
          <div className="flex shrink-0 items-center justify-between border-b border-[#e5e7eb] bg-[#f3f4f6] px-5 py-2.5">
            <label className="flex items-center gap-2 text-sm font-medium text-[#17233b]">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(event) =>
                  setSelectedKeys(event.target.checked ? selectableKeys : [])
                }
                className="h-4 w-4 rounded border-[#c5d0dc] text-[#174a87] focus:ring-[#174a87]"
              />
              {selectedKeys.length} selected
            </label>
            <div className="flex items-center gap-3 text-sm font-medium text-[#17233b]">
              {canRestore ? (
                <button
                  type="button"
                  title="Restore selected"
                  onClick={() => requestBulkMove('undelete')}
                  className="inline-flex items-center gap-1.5 hover:text-[#174a87]"
                >
                  <Undo2 className="h-4 w-4" />
                  Restore
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    title="Mark selected as Read"
                    onClick={() => requestBulkMove('read')}
                    className="inline-flex items-center gap-1.5 hover:text-[#174a87]"
                  >
                    <Check className="h-4 w-4" />
                    Read
                  </button>
                  <button
                    type="button"
                    title="Delete selected"
                    onClick={() => requestDelete(selectedKeys)}
                    className="rounded-lg p-0.5 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <div
          ref={listScrollRef}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        >
        {loading ? (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-[#75839a]">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading...
          </div>
        ) : visibleNotifications.length === 0 ? (
          <div className="flex h-full items-center justify-center p-12 text-center text-sm text-[#75839a]">
            No notifications here!
          </div>
        ) : (
          <>
            <ul className="divide-y divide-[#e8edf3]">
              {visibleNotifications.map((item) => {
                const sender = getSenderName(item.first_name, item.last_name)
                const unread = isCenterUnread(item)
                const selected = selectedKey === `${item.kind}-${item.id}`
                const isUser = item.kind === 'user'
                return (
                  <li key={`${item.kind}-${item.id}`}>
                    <div
                      className={cn(
                        'relative flex items-start gap-3 px-5 py-3.5 transition-colors',
                        selected
                          ? 'bg-[#d7e4f6]'
                          : unread
                            ? 'bg-[#eef5ff]'
                            : 'bg-white hover:bg-[#f7f9fc]',
                      )}
                    >
                      {selected ? (
                        <span className="absolute inset-y-0 left-0 w-1 bg-[#174a87]" />
                      ) : unread ? (
                        <span className="absolute inset-y-0 left-0 w-1 bg-[#2f7de1]" />
                      ) : null}
                      <input
                        type="checkbox"
                        checked={selectedKeys.includes(selectionKey(item))}
                        onChange={(event) =>
                          setSelectedKeys((keys) =>
                            event.target.checked
                              ? [...keys, selectionKey(item)]
                              : keys.filter((key) => key !== selectionKey(item)),
                          )
                        }
                        className="mt-3 h-4 w-4 rounded border-[#c5d0dc] text-[#174a87] focus:ring-[#174a87]"
                        onClick={(event) => event.stopPropagation()}
                      />
                      <button
                        type="button"
                        onClick={() => void openDetail(item)}
                        className="flex min-w-0 flex-1 items-start gap-3 text-left"
                      >
                        <SenderAvatar
                          firstName={item.first_name}
                          lastName={item.last_name}
                          size="sm"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <p className="truncate text-sm font-bold">
                              {decodeNotificationText(item.title) ||
                                getNotificationTitle(item.subject)}
                            </p>
                            <span className="shrink-0 text-xs text-[#8491a5]">
                              {formatNotificationListDate(item.created_at)}
                            </span>
                          </div>
                          <p className="mt-0.5 flex items-center gap-2 text-sm font-semibold">
                            <span className="truncate">{sender}</span>
                            {unread && (
                              <span className="size-1.5 shrink-0 rounded-full bg-[#2f7de1]" />
                            )}
                          </p>
                          <p className="mt-0.5 truncate text-sm text-[#75839a]">
                            {decodeNotificationText(item.preview) ||
                              (isUser
                                ? buildNotificationPreviewText(toUserNotification(item))
                                : '')}
                          </p>
                        </div>
                      </button>
                      {folder !== 'deleted' && isUser && (
                        <button
                          type="button"
                          title={
                            isStarredMessage(item) ? 'Remove from Starred' : 'Move to Starred'
                          }
                          onClick={() => void toggleInboxStar(item).catch(showApiError)}
                          className="mt-1 shrink-0 text-[#8491a5] hover:text-amber-500"
                        >
                          <Star
                            className={cn(
                              'h-4 w-4',
                              isStarredMessage(item) && 'fill-amber-400 text-amber-400',
                            )}
                          />
                        </button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
            <div ref={loadMoreSentinelRef} className="h-4" />
            {loadingMore && (
              <div className="flex items-center justify-center gap-2 py-3 text-sm text-[#75839a]">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading more...
              </div>
            )}
          </>
        )}
        </div>
      </section>

      <aside
        data-testid="profile-messages-detail"
        className={cn(
          'min-h-0 min-w-0 overflow-y-auto rounded-2xl border border-[#dfe6ef] bg-white lg:h-full',
          !detailSource && 'hidden lg:block',
        )}
      >
        {!detailSource ? (
          <div className="flex min-h-[28rem] flex-col items-center justify-center gap-2 p-8 text-center text-sm text-[#75839a]">
            <Bell className="h-8 w-8 text-[#c5d0dc]" />
            Select a notification to view details
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-[#e8edf3] px-4 py-3">
              <span className="rounded-full bg-[#eef2f6] px-3 py-1 text-xs font-semibold text-[#536176]">
                {getNotificationCategoryLabel(detailSource.kind, detailSource.notes_for)}
              </span>
              <div className="relative flex items-center gap-1 text-[#536176]">
                {folder === 'deleted' && (
                  <button
                    type="button"
                    title="Restore"
                    onClick={() => void undeleteCurrent()}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium hover:bg-[#f4f7fb] hover:text-[#174a87]"
                  >
                    <Undo2 className="h-4 w-4" />
                    Restore
                  </button>
                )}
                {folder !== 'deleted' && (
                  <>
                    {detailSource.kind === 'user' && (
                      <button
                        type="button"
                        title={
                          isStarredMessage(detailSource)
                            ? 'Remove from Starred'
                            : 'Move to Starred'
                        }
                        onClick={() =>
                          void updateDetail(
                            isStarredMessage(detailSource) ? 'not_starred' : 'starred',
                          )
                        }
                        className="rounded-lg p-1.5 hover:bg-[#f4f7fb]"
                      >
                        <Star
                          className={cn(
                            'h-4 w-4',
                            isStarredMessage(detailSource) && 'fill-amber-400 text-amber-400',
                          )}
                        />
                      </button>
                    )}
                    <button
                      type="button"
                      title="Move to Deleted"
                      onClick={() => void updateDetail('deleted')}
                      className="rounded-lg p-1.5 hover:bg-[#f4f7fb] hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      title="More actions"
                      onClick={() => setMoreOpen((value) => !value)}
                      className="rounded-lg p-1.5 hover:bg-[#f4f7fb]"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    {moreOpen && (
                      <div className="absolute right-0 top-9 z-10 w-44 rounded-xl border border-[#dfe6ef] bg-white py-1 shadow-lg">
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#f4f7fb]"
                          onClick={() => void updateDetail('unread')}
                        >
                          <Inbox className="h-4 w-4" /> Mark as unread
                        </button>
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#f4f7fb]"
                          onClick={() => void updateDetail('read')}
                        >
                          <MailOpen className="h-4 w-4" /> Mark as Read
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {detailLoading ? (
              <div className="flex min-h-[20rem] items-center justify-center gap-2 text-sm text-[#75839a]">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading...
              </div>
            ) : detailSource.kind === 'whats_new' ? (
              <div className="space-y-5 p-5">
                <div className="flex items-start gap-3">
                  <SenderAvatar
                    firstName={detailSource.first_name}
                    lastName={detailSource.last_name}
                    size="lg"
                  />
                  <div className="min-w-0">
                    <h3 className="text-lg font-bold leading-snug">
                      {decodeNotificationText(detailSource.title)}
                    </h3>
                    {detailSource.version_type && (
                      <p
                        className={cn(
                          'mt-1 text-sm font-medium',
                          VERSION_TYPE_COLOR_CLASS[detailSource.version_type] ?? 'text-[#536176]',
                        )}
                      >
                        {VERSION_TYPE_LABELS[detailSource.version_type] ??
                          detailSource.version_type}
                      </p>
                    )}
                    <p className="mt-1 text-sm text-[#75839a]">
                      From{' '}
                      <span className="font-semibold text-[#17233b]">Liberty Rent</span>
                      {' · '}
                      {formatNotificationDetailDate(detailSource.created_at)}
                    </p>
                  </div>
                </div>
                {detailSource.body && (
                  <div
                    className="prose prose-sm max-w-none min-w-0 break-words [overflow-wrap:anywhere] text-sm text-[#17233b]"
                    dangerouslySetInnerHTML={{
                      __html: processReleaseNoteImages(detailSource.body),
                    }}
                  />
                )}
              </div>
            ) : detailSource.kind === 'system' ? (
              <div className="space-y-5 p-5">
                <div className="flex items-start gap-3">
                  <SenderAvatar
                    firstName={detailSource.first_name}
                    lastName={detailSource.last_name}
                    size="lg"
                  />
                  <div className="min-w-0">
                    <h3 className="text-lg font-bold leading-snug">
                      {decodeNotificationText(detailSource.title)}
                    </h3>
                    <p className="mt-1 text-sm text-[#75839a]">
                      {detailSource.start_date}
                      {detailSource.end_date ? ` to ${detailSource.end_date}` : ''}
                    </p>
                  </div>
                </div>
                {detailSource.body && (
                  <div
                    className="announcement-html max-w-none min-w-0 break-words [overflow-wrap:anywhere] text-sm text-[#17233b]"
                    dangerouslySetInnerHTML={{
                      __html: normalizeAnnouncementDescriptionHtml(detailSource.body),
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="space-y-5 p-5">
                <div className="flex items-start gap-3">
                  <SenderAvatar
                    firstName={detailSource.first_name}
                    lastName={detailSource.last_name}
                    size="lg"
                  />
                  <div className="min-w-0">
                    <h3 className="text-lg font-bold leading-snug">
                      {getNotificationTitle(detailSource.subject) || section?.subtitle}
                    </h3>
                    <p className="mt-1 text-sm text-[#75839a]">
                      From{' '}
                      <span className="font-semibold text-[#17233b]">
                        {getSenderName(detailSource.first_name, detailSource.last_name)}
                      </span>
                      {' · '}
                      {formatNotificationDetailDate(detailSource.created_at)}
                    </p>
                  </div>
                </div>

                <p className="text-sm text-[#536176]">
                  {buildNotificationPreviewText(toUserNotification(detailSource))}.
                </p>

                {detail?.notes && (
                  <div className="rounded-xl bg-[#f4f7fb] p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[.14em] text-[#8491a5]">
                      Note
                    </p>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-[#17233b]">
                      {stripHtml(detail.notes)}
                    </p>
                  </div>
                )}

                {section && (section.refLink || section.hereLink) && (
                  <div className="flex items-center justify-between gap-3 rounded-xl bg-[#e8f1ff] p-4">
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-[.14em] text-[#8491a5]">
                        Related record
                      </p>
                      {section.refLink && detail?.ref_name && (
                        <p className="mt-1 font-semibold">{detail.ref_name}</p>
                      )}
                      <p className="mt-1 text-sm text-[#75839a]">
                        Open the full context of this notification.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-[#163a70] px-3 py-2 text-sm font-semibold text-white"
                      onClick={() =>
                        goToTarget((section.hereLink ?? section.refLink)!.href)
                      }
                    >
                      View details
                      <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </aside>

      <ConfirmDialog
        isOpen={moveOpen}
        title={
          pendingMove === 'undelete'
            ? 'Restore Notifications'
            : pendingMove === 'read'
              ? 'Move to Read'
              : 'Mark as unread'
        }
        message={
          pendingMove === 'undelete'
            ? selectedKeys.length === 1
              ? 'Are you sure to restore this notification?'
              : `Are you sure to restore selected ${selectedKeys.length} notification?`
            : pendingMove === 'read'
              ? `Are you sure to move selected ${selectedKeys.length} notification to Read?`
              : `Are you sure to mark selected ${selectedKeys.length} notification as unread?`
        }
        primaryLabel="Yes"
        secondaryLabel="No"
        onPrimary={() => void moveSelected()}
        onSecondary={() => {
          setMoveOpen(false)
          setPendingMove(null)
        }}
        primaryLoading={moving}
      />
      <ConfirmDialog
        isOpen={deleteOpen}
        title="Delete Notifications"
        message={
          pendingDeleteKeys.length === 1
            ? 'Are you sure to delete this notification?'
            : `Are you sure to delete selected ${pendingDeleteKeys.length} notification?`
        }
        primaryLabel="Yes"
        secondaryLabel="No"
        onPrimary={() => void deleteSelected()}
        onSecondary={() => {
          setDeleteOpen(false)
          setPendingDeleteKeys([])
        }}
        primaryLoading={deleting}
      />
    </div>
  )
}
